/** WorkPanel — the "what's on" surface: open obligations + week brief + pipeline freshness. */
import { useMcpTool } from "../lib/useMcpTool";
import McpCard from "../components/McpCard";
import DataTable, { Column } from "../components/DataTable";
import BriefNarrative from "../components/BriefNarrative";
import StatusDot from "../components/StatusDot";
import { PanelScroll } from "./ClientsPanel";

interface Obligation { id: number; folder: string; text: string; status: string; next_step?: string; due_date?: string }
interface Freshness { fresh_count: number; stale_count: number; error_count: number; pipelines: Array<{ table: string; label: string; age_hours: number; status: string }> }

export default function WorkPanel() {
  const obligations = useMcpTool<Obligation[]>("business", "get_open_obligations", { limit: 100 });
  const week = useMcpTool<string>("business", "get_week_brief");
  const fresh = useMcpTool<Freshness>("core", "pipeline_freshness");
  const rows = Array.isArray(obligations.data) ? obligations.data : [];
  const cols: Column<Obligation>[] = [
    { header: "Client", render: (o) => <span className="font-mono text-caption text-text-muted">{o.folder}</span> },
    { header: "Obligation", render: (o) => <div><div className="text-text">{o.text}</div>{o.next_step && <div className="text-caption text-text-dim mt-0.5">→ {o.next_step}</div>}</div> },
    { header: "Status", render: (o) => <Badge s={o.status} /> },
    { header: "Due", render: (o) => <span className="font-mono text-caption">{o.due_date ?? "—"}</span> },
  ];
  return (
    <PanelScroll accentVar="--biome-business" title="Work" subtitle="obligations · week brief · pipelines">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <McpCard title={`Open obligations${rows.length ? ` (${rows.length})` : ""}`} loading={obligations.loading} error={obligations.error} onRefresh={obligations.refresh} accentVar="--biome-business">
          <DataTable columns={cols} rows={rows} empty="No open obligations." />
        </McpCard>
        <div className="space-y-4">
          <McpCard title="Pipeline freshness" loading={fresh.loading} error={fresh.error} onRefresh={fresh.refresh}>
            {fresh.data && (
              <div className="space-y-2">
                <div className="text-body-sm text-text-muted">{fresh.data.fresh_count} fresh · {fresh.data.stale_count} stale · {fresh.data.error_count} error</div>
                {fresh.data.pipelines?.map((p) => (
                  <div key={p.table} className="flex items-center gap-2 text-body-sm">
                    <StatusDot health={p.status === "fresh" ? "green" : p.status === "stale" ? "yellow" : "red"} />
                    <span className="flex-1 truncate">{p.label}</span>
                    <span className="font-mono text-caption text-text-dim">{p.age_hours < 1 ? `${Math.round(p.age_hours * 60)}m` : p.age_hours < 48 ? `${p.age_hours.toFixed(1)}h` : `${Math.round(p.age_hours / 24)}d`}</span>
                  </div>
                ))}
              </div>
            )}
          </McpCard>
          <McpCard title="Week brief" loading={week.loading} error={week.error} onRefresh={week.refresh} accentVar="--biome-business">
            {typeof week.data === "string" ? <BriefNarrative text={week.data} /> : <div className="text-body-sm text-text-dim">No brief.</div>}
          </McpCard>
        </div>
      </div>
    </PanelScroll>
  );
}

function Badge({ s }: { s: string }) {
  const style: Record<string, string> = { BLOCKED: "bg-danger-muted text-danger", IN_PROGRESS: "bg-info-muted text-info", NOT_STARTED: "bg-warning-muted text-warning", UNKNOWN: "bg-surface-hover text-text-muted" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ${style[s] ?? "bg-surface-hover text-text-muted"}`}>{s.replace("_", " ")}</span>;
}
